import { useState, useEffect } from 'react'
import { FloppyDiskIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopTextField,
    ScoopSelector,
    ScoopText,
    ScoopSpinner,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
    ScoopAccordion,
} from '../../../../../ui'
import { FormField } from '../shared'
import {
    parseMetadataToForm,
    buildMetadataFromForm,
    listDatasets,
    ADVANCED_SECTIONS,
} from './metadataFormModel'
import styles from './MetadataForm.module.scss'

const MONTH_OPTIONS = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
]

const MONTH_NAMES = MONTH_OPTIONS.map((m) => m.label)

/**
 * MetadataForm — edits the fields metadata.json actually contains (2026-07 DI
 * UI audit F15). Fiscal year is the month it ENDS (production contract); the
 * old phantom start_month/convention/description/industry fields are gone.
 * Saving merges — untouched keys (rollup_guardrails, terminology,
 * action_planning, datasets, ...) are preserved verbatim and shown read-only.
 */
export const MetadataForm = ({ data, onSave, isSaving, isLoading, onDirtyStateChange }) => {
    const [formData, setFormData] = useState(() => parseMetadataToForm(null))
    const [hasChanges, setHasChanges] = useState(false)

    useEffect(() => {
        if (data) {
            setFormData(parseMetadataToForm(data))
            setHasChanges(false)
        }
    }, [data])

    useEffect(() => {
        onDirtyStateChange?.(hasChanges)
    }, [hasChanges, onDirtyStateChange])

    const setTop = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        setHasChanges(true)
    }

    const setSection = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: { ...prev[section], [field]: value },
        }))
        setHasChanges(true)
    }

    const handleEndMonthChange = (value) => {
        const month = parseInt(value, 10)
        setFormData((prev) => ({
            ...prev,
            fiscal_year: {
                ...prev.fiscal_year,
                end_month: month,
                // Keep the denormalized name in sync with the month.
                end_month_name: MONTH_NAMES[month - 1] || prev.fiscal_year.end_month_name,
            },
        }))
        setHasChanges(true)
    }

    const handleSave = () => {
        onSave(buildMetadataFromForm(data, formData))
        setHasChanges(false)
    }

    const handleReset = () => {
        if (data) {
            setFormData(parseMetadataToForm(data))
            setHasChanges(false)
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set'
        const date = new Date(dateString)
        return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString()
    }

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading metadata...</ScoopText>
            </ScoopCenter>
        )
    }

    const datasets = listDatasets(data?.datasets)
    const advancedPresent = ADVANCED_SECTIONS.filter(([key]) => data?.[key])

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        Workspace Overview
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        Configure basic information about this investigation workspace.
                    </ScoopText>
                </div>
                <ScoopRow className={styles.actions}>
                    {hasChanges && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={handleReset}
                            disabled={isSaving}
                            leftSection={<ArrowCounterClockwiseIcon size={16} />}
                        >
                            Reset
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant="default"
                        size="sm"
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                        leftSection={<FloppyDiskIcon size={16} />}
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </div>

            <div className={styles.content}>
                {/* Basic information */}
                <div className={styles.section}>
                    <ScoopText as="h3" className={styles.sectionTitle}>
                        Basic Information
                    </ScoopText>
                    <ScoopColumn className={styles.fields}>
                        <FormField label="Workspace Name" helpText="Display name for this workspace">
                            <ScoopTextField
                                value={formData.workspace_name}
                                onChange={(e) => setTop('workspace_name', e.target.value)}
                                placeholder="Enter workspace name"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopColumn>
                </div>

                {/* Data coverage */}
                <div className={styles.section}>
                    <ScoopText as="h3" className={styles.sectionTitle}>
                        Data Coverage
                    </ScoopText>
                    <ScoopColumn className={styles.fields}>
                        <ScoopRow className={styles.dateRow}>
                            <FormField
                                label="Earliest Date"
                                helpText="Earliest date in the data (affects YoY comparisons)"
                            >
                                <ScoopTextField
                                    value={formData.data_coverage.earliest_date}
                                    onChange={(e) =>
                                        setSection('data_coverage', 'earliest_date', e.target.value)
                                    }
                                    placeholder="YYYY-MM-DD"
                                    disabled={isSaving}
                                />
                            </FormField>
                            <FormField label="Latest Date" helpText="Latest date in the data">
                                <ScoopTextField
                                    value={formData.data_coverage.latest_date}
                                    onChange={(e) =>
                                        setSection('data_coverage', 'latest_date', e.target.value)
                                    }
                                    placeholder="YYYY-MM-DD"
                                    disabled={isSaving}
                                />
                            </FormField>
                            <FormField label="Granularity" helpText="Snapshot cadence of the data">
                                <ScoopTextField
                                    value={formData.data_coverage.granularity}
                                    onChange={(e) =>
                                        setSection('data_coverage', 'granularity', e.target.value)
                                    }
                                    placeholder="biweekly snapshots"
                                    disabled={isSaving}
                                />
                            </FormField>
                        </ScoopRow>
                        <FormField label="Data Notes" helpText="Context about data availability">
                            <ScoopTextField
                                value={formData.data_coverage.note}
                                onChange={(e) =>
                                    setSection('data_coverage', 'note', e.target.value)
                                }
                                placeholder="Notes about data coverage"
                                multiline
                                rows={2}
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopColumn>
                </div>

                {/* Fiscal year — the month it ENDS (production contract) */}
                <div className={styles.section}>
                    <ScoopText as="h3" className={styles.sectionTitle}>
                        Fiscal Year
                    </ScoopText>
                    <ScoopColumn className={styles.fields}>
                        <ScoopRow className={styles.selectRow}>
                            <FormField
                                label="Fiscal Year End Month"
                                helpText="Month the fiscal year ends"
                            >
                                <ScoopSelector
                                    value={formData.fiscal_year.end_month}
                                    onChange={(e) => handleEndMonthChange(e.target.value)}
                                    options={MONTH_OPTIONS}
                                    placeholder="Select month"
                                    disabled={isSaving}
                                />
                            </FormField>
                            <FormField
                                label="Quarterly Naming"
                                helpText="How fiscal quarters are labeled"
                            >
                                <ScoopTextField
                                    value={formData.fiscal_year.quarterly_naming}
                                    onChange={(e) =>
                                        setSection('fiscal_year', 'quarterly_naming', e.target.value)
                                    }
                                    placeholder="e.g. Q1 = first fiscal quarter"
                                    disabled={isSaving}
                                />
                            </FormField>
                        </ScoopRow>
                        <FormField label="Fiscal Year Note">
                            <ScoopTextField
                                value={formData.fiscal_year.note}
                                onChange={(e) => setSection('fiscal_year', 'note', e.target.value)}
                                placeholder="How fiscal years map to calendar dates"
                                multiline
                                rows={2}
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopColumn>
                </div>

                {/* Date conventions — balance vs flow metric query semantics */}
                <div className={styles.section}>
                    <ScoopText as="h3" className={styles.sectionTitle}>
                        Date Conventions
                    </ScoopText>
                    <ScoopText className={styles.sectionDescription}>
                        How balance (point-in-time) and flow (period) metrics are queried.
                    </ScoopText>
                    <ScoopColumn className={styles.fields}>
                        <FormField label="Balance Metrics" helpText="Point-in-time metrics (e.g. PLO)">
                            <ScoopTextField
                                value={formData.balance_metrics.description}
                                onChange={(e) =>
                                    setSection('balance_metrics', 'description', e.target.value)
                                }
                                placeholder="Description"
                                disabled={isSaving}
                            />
                        </FormField>
                        <ScoopRow className={styles.selectRow}>
                            <FormField label="Balance Query Date">
                                <ScoopTextField
                                    value={formData.balance_metrics.query_date}
                                    onChange={(e) =>
                                        setSection('balance_metrics', 'query_date', e.target.value)
                                    }
                                    placeholder="most recent snapshot"
                                    disabled={isSaving}
                                />
                            </FormField>
                            <FormField label="Posting Convention">
                                <ScoopTextField
                                    value={formData.balance_metrics.posting_convention}
                                    onChange={(e) =>
                                        setSection(
                                            'balance_metrics',
                                            'posting_convention',
                                            e.target.value
                                        )
                                    }
                                    disabled={isSaving}
                                />
                            </FormField>
                        </ScoopRow>
                        <FormField label="Flow Metrics" helpText="Period metrics (e.g. NLM, Sales)">
                            <ScoopTextField
                                value={formData.flow_metrics.description}
                                onChange={(e) =>
                                    setSection('flow_metrics', 'description', e.target.value)
                                }
                                placeholder="Description"
                                disabled={isSaving}
                            />
                        </FormField>
                        <FormField label="Flow Query Range">
                            <ScoopTextField
                                value={formData.flow_metrics.query_range}
                                onChange={(e) =>
                                    setSection('flow_metrics', 'query_range', e.target.value)
                                }
                                placeholder="full period range"
                                disabled={isSaving}
                            />
                        </FormField>
                    </ScoopColumn>
                </div>

                {/* System information (read-only) */}
                {data && (
                    <div className={styles.section}>
                        <ScoopText as="h3" className={styles.sectionTitle}>
                            System Information
                        </ScoopText>
                        <ScoopText className={styles.sectionDescription}>
                            These values are managed by the system and cannot be edited.
                        </ScoopText>
                        <div className={styles.readOnlyGrid}>
                            <div className={styles.readOnlyField}>
                                <ScoopText className={styles.readOnlyLabel}>Schema Version</ScoopText>
                                <ScoopText className={styles.readOnlyValue}>
                                    {data.schema_version || 'N/A'}
                                </ScoopText>
                            </div>
                            <div className={styles.readOnlyField}>
                                <ScoopText className={styles.readOnlyLabel}>Workspace ID</ScoopText>
                                <ScoopText className={styles.readOnlyValue}>
                                    {data.workspace_id || 'N/A'}
                                </ScoopText>
                            </div>
                            <div className={styles.readOnlyField}>
                                <ScoopText className={styles.readOnlyLabel}>
                                    Primary Inbox ID
                                </ScoopText>
                                <ScoopText className={styles.readOnlyValue}>
                                    {data.primary_inbox_id || 'N/A'}
                                </ScoopText>
                            </div>
                            <div className={styles.readOnlyField}>
                                <ScoopText className={styles.readOnlyLabel}>Last Validated</ScoopText>
                                <ScoopText className={styles.readOnlyValue}>
                                    {formatDate(data.last_validated)}
                                </ScoopText>
                            </div>
                        </div>

                        {/* Linked datasets — production shape: object keyed by role */}
                        {datasets.length > 0 && (
                            <div className={styles.datasetsSection}>
                                <ScoopText className={styles.readOnlyLabel}>
                                    Linked Datasets
                                </ScoopText>
                                <div className={styles.datasetsList}>
                                    {datasets.map((dataset) => (
                                        <div key={dataset.key} className={styles.datasetItem}>
                                            <ScoopText className={styles.datasetName}>
                                                {dataset.name}
                                            </ScoopText>
                                            {dataset.inboxID && (
                                                <ScoopText className={styles.datasetId}>
                                                    {dataset.inboxID}
                                                </ScoopText>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Advanced configuration (read-only viewers — full editors are follow-up) */}
                {advancedPresent.length > 0 && (
                    <div className={styles.section}>
                        <ScoopText as="h3" className={styles.sectionTitle}>
                            Advanced Configuration
                        </ScoopText>
                        <ScoopText className={styles.sectionDescription}>
                            These sections shape every generated report. Shown read-only here;
                            they are preserved exactly on save.
                        </ScoopText>
                        {advancedPresent.map(([key, label, help]) => (
                            <ScoopAccordion key={key}>
                                <ScoopAccordion.Summary>
                                    <div className={styles.advancedSummary}>
                                        <ScoopText className={styles.advancedTitle}>
                                            {label}
                                        </ScoopText>
                                        <ScoopText className={styles.advancedHelp}>{help}</ScoopText>
                                    </div>
                                </ScoopAccordion.Summary>
                                <ScoopAccordion.Details>
                                    <pre className={styles.advancedJson}>
                                        {JSON.stringify(data[key], null, 2)}
                                    </pre>
                                </ScoopAccordion.Details>
                            </ScoopAccordion>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
